import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormHelperText,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import axios from "axios";
import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { Eye, EyeOff, Info } from "react-feather";
import { Context } from "../ContextWrapper";
import { useLocation } from "react-router-dom";
import { login } from "../api/authentication";
import { signup } from "../api/profile";

export const WelcomeLegacyForm = () => {
  const context = useContext(Context);
  const location = useLocation();

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    showPassword: false,
    keepMeSignedIn: false,
    loginLoading: false
  });

  const [signupData, setSignupData] = useState({
    name: "",
    surname: "",
    email: "",
    phone_number: "",
    password: "",
    confirmPassword: "",
  });
  const [isPassValid, setIsPassValid] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // --- Dynamic dashTitle centering and scaling ---
  const [dashTitleSize, setDashTitleSize] = useState({ width: 900, height: undefined });

  const rightPanelRef = useRef(null);
  const dashTitleRef = useRef(null);
  const metricsRef = useRef(null);
  const [dashTitleTop, setDashTitleTop] = useState(undefined);
  const [dashTitleLoaded, setDashTitleLoaded] = useState(false);
  const [metricsLoaded, setMetricsLoaded] = useState(false);

  // Helper to center dashTitle
  const centerDashTitle = useCallback(() => {
    if (!rightPanelRef.current || !dashTitleRef.current || !metricsRef.current) {
      return;
    }
    const panelRect = rightPanelRef.current.getBoundingClientRect();
    const dashTitleRect = dashTitleRef.current.getBoundingClientRect();
    const metricsRect = metricsRef.current.getBoundingClientRect();
    const availableSpace = metricsRect.top - panelRect.top;
    const dashTitleHeight = dashTitleRect.height;
    const top = (availableSpace - dashTitleHeight) / 2;
    setDashTitleTop(top);
  }, []);

  // Run centering after both images are loaded and on resize
  useEffect(() => {
    if (metricsLoaded) {
      centerDashTitle();
      window.addEventListener("resize", centerDashTitle);
      return () => window.removeEventListener("resize", centerDashTitle);
    }
  }, [metricsLoaded, centerDashTitle]);

  useEffect(() => {
    function updateDashTitleSize() {
      if (!rightPanelRef.current) return;
      const panelRect = rightPanelRef.current.getBoundingClientRect();
      // 8% margin on each side
      const marginX = panelRect.width * 0.08;
      const marginY = panelRect.height * 0.08;
      const availableWidth = panelRect.width - 2 * marginX;
      const availableHeight = panelRect.height - 2 * marginY;
      // Ideal SVG size
      const idealWidth = 900;
      const idealHeight = 0.25 * panelRect.height; // or a fixed value if you know the SVG's aspect ratio
      // Only scale down if both width and height are constrained
      let width = Math.min(idealWidth, availableWidth);
      let height = idealHeight;
      if (width < idealWidth || availableHeight < idealHeight) {
        // Scale down proportionally
        const widthRatio = availableWidth / idealWidth;
        const heightRatio = availableHeight / idealHeight;
        const scale = Math.min(widthRatio, heightRatio, 1);
        width = idealWidth * scale;
        height = idealHeight * scale;
      }
      setDashTitleSize({ width, height });
    }
    updateDashTitleSize();
    window.addEventListener("resize", updateDashTitleSize);
    return () => window.removeEventListener("resize", updateDashTitleSize);
  }, []);

  const handleShowClick = () => {
    setLoginData(prev => ({
      ...prev,
      showPassword: !prev.showPassword
    }));
  };

  const handleShowSignupPassword = () =>
    setShowSignupPassword(!showSignupPassword);
  const handleShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const allFieldsFilled = Object.values(signupData).every(
    (field) => field.length > 0
  );

  const handleLoginInputChange = (field) => (e) => {
    setLoginData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleEmailBlur = (field, isLogin = false) => () => {
    const setState = isLogin ? setLoginData : setSignupData;
    setState(prev => ({
      ...prev,
      [field]: prev[field].toLowerCase()
    }));
  };

  const handleEmailKeyDown = (field, isLogin = false) => (e) => {
    if (e.key === 'Enter') {
      const setState = isLogin ? setLoginData : setSignupData;
      setState(prev => ({
        ...prev,
        [field]: prev[field].toLowerCase()
      }));
    }
  };

  const handleInputChange = (field) => (e) => {
    setSignupData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const getPasswordRequirements = (password, confirmPassword) => {
    const hasMinLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasCapital = /[A-Z]/.test(password);
    const passwordsMatch = password === confirmPassword && password.length > 0;

    const isValid =
      hasMinLength &&
      hasSpecialChar &&
      hasNumber &&
      hasCapital &&
      passwordsMatch;

    return {
      hasMinLength,
      hasSpecialChar,
      hasNumber,
      hasCapital,
      passwordsMatch,
      isValid,
    };
  };

  const checkPasswordRequirements = useCallback(
    (password, confirmPassword) => {
      const requirements = getPasswordRequirements(password, confirmPassword);
      setIsPassValid(requirements.isValid);
      return requirements;
    },
    []
  );

  useEffect(() => {
    if (signupData.password || signupData.confirmPassword) {
      checkPasswordRequirements(
        signupData.password,
        signupData.confirmPassword
      );
    }
  }, [
    signupData.password,
    signupData.confirmPassword,
    checkPasswordRequirements,
  ]);

  // Handle autofill detection for tooltip positioning
  useEffect(() => {
    const handleAutofill = () => {
      // Force tooltip repositioning after autofill
      if (signupData.password.length > 0) {
        // Trigger a small delay to let autofill complete
        setTimeout(() => {
          // Force re-render of tooltip by briefly toggling a state
          setSignupData(prev => ({ ...prev, password: prev.password }));
        }, 100);
      }
    };

    // Listen for autofill events
    const passwordInput = document.querySelector('input[placeholder="Create password"]');
    if (passwordInput) {
      passwordInput.addEventListener('input', handleAutofill);
      // Also listen for focus events which can indicate autofill
      passwordInput.addEventListener('focus', handleAutofill);
    }

    return () => {
      if (passwordInput) {
        passwordInput.removeEventListener('input', handleAutofill);
        passwordInput.removeEventListener('focus', handleAutofill);
      }
    };
  }, [signupData.password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginData(prev => ({ ...prev, loginLoading: true }));

    const user = {
      username: loginData.email.toLowerCase(),
      password: loginData.password,
    };

    try {
      const res = await login(user);

      if (res.status === 200) {
        localStorage.clear();
        sessionStorage.clear();

        if (loginData.keepMeSignedIn) {
          localStorage.setItem("access_token", res.data.access);
          localStorage.setItem("refresh_token", res.data.refresh);
        } else {
          sessionStorage.setItem("access_token", res.data.access);
          sessionStorage.setItem("refresh_token", res.data.refresh);
        }

        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${res.data.access}`;

        window.location.href = "/";
      }
    } catch (error) {
      context.fireToast("Error", "Invalid credentials.", "error");
    } finally {
      setLoginData(prev => ({ ...prev, loginLoading: false }));
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupLoading(true);

    const phoneRegex = /^\+?\d{1,3}\d{9,15}$/;
    if (!phoneRegex.test(signupData.phone_number)) {
      context.fireToast("Error", "Please enter a valid phone number.", "error");
      setSignupLoading(false);
      return;
    }

    const requirements = checkPasswordRequirements(
      signupData.password,
      signupData.confirmPassword
    );
    if (!requirements.isValid) {
      context.fireToast(
        "Error",
        "Please ensure all password requirements are met.",
        "error"
      );
      setSignupLoading(false);
      return;
    }

    try {
      await signup({
        name: signupData.name,
        surname: signupData.surname,
        email: signupData.email.toLowerCase(),
        phone_number: signupData.phone_number,
        password: signupData.password,
        legacy_user: true,
      });

      setSignupSuccess(true);
    } catch (err) {
      if (err.response) {
        const errorMessage =
          err.response.data?.error ||
          err.response.data?.email?.[0] ||
          err.response.data?.phone_number?.[0] ||
          err.response.data?.details ||
          "Failed to create account. Please try again.";

        context.fireToast("Error", errorMessage, "error");
      } else {
        context.fireToast(
          "Error",
          "Network error. Please check your connection and try again.",
          "error"
        );
      }
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <Container
      maxW="100vw"
      p={0}
      height="100vh"
      overflow="hidden"
      position="relative"
      bg="#FFFFFF"
    >
      <Box display="flex" direction="row" height="100vh" overflow="hidden">
        <Box
          w={{ base: "100%", lg: "45%" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={{ base: 2, md: 4 }}
          position="relative"
          overflow="auto"
          bg="#FFFFFF"
          height="100vh"
          sx={{
            "@media screen and (max-aspect-ratio: 1/1)": {
              width: "100% !important"
            },
            "@media screen and (max-height: 700px)": {
              alignItems: "flex-start",
              paddingTop: "2rem"
            }
          }}
        >
          <Stack
            flexDir="column"
            mb="2"
            justifyContent="center"
            alignItems="center"
            w="100%"
            maxW="none"
          >
            {signupSuccess ? (
              <Stack
                spacing={4}
                p="1.75rem"
                width="100%"
                maxW="550px"
                backgroundColor="#FFFFFF"
                boxShadow="0px 8px 16px 0px #00000026"
                borderRadius="5px"
                alignItems="center"
              >
                <Box
                  w="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  mb={2}
                >
                  <Image
                    width="221px"
                    src="lucro-logo-light.png"
                    alt="Lucro Logo"
                  />
                </Box>
                <Text
                  fontWeight={500}
                  fontSize="24px"
                  color="#234E94"
                  textAlign="center"
                >
                  Check Your Email
                </Text>
                <Text fontSize="16px" color="#666666" textAlign="center">
                  We've sent you an activation link at{" "}
                  <strong>{signupData.email}</strong>. Please check your email and
                  click the link to complete your registration.
                </Text>
                <FormControl w="100%">
                  <FormHelperText
                    textAlign="center"
                    fontSize="14px"
                    fontWeight={400}
                    color="#666666"
                  >
                    Didn't receive the email? Check your spam folder or try again
                    with a different email address.
                  </FormHelperText>
                </FormControl>
                <Button
                  mt={3}
                  borderRadius="4px"
                  variant="outline"
                  borderColor="#536DEC"
                  color="#536DEC"
                  _hover={{ bg: "#536DEC", color: "white" }}
                  w="100%"
                  onClick={() => setSignupSuccess(false)}
                >
                  Back to Sign Up
                </Button>
              </Stack>
            ) : (
              <Stack
                spacing={4}
                p={{ base: "1rem", md: "2rem", lg: "3rem" }}
                width={{ base: "95%", md: "90%" }}
                minWidth={{ base: "280px", md: "400px" }}
                maxWidth="600px"
                backgroundColor="#FFFFFF"
                borderRadius="5px"
                alignItems="center"
                position="relative"
                overflow="visible"
                maxH={{ base: "90vh", md: "none" }}
              >
                <Box
                  w="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  mb={2}
                >
                  <Text fontSize="24px" fontWeight={400}>
                    Welcome to
                  </Text>
                  <Image
                    width="221px"
                    src="lucro-logo-light.png"
                    alt="Lucro Logo"
                  />
                </Box>

                <Tabs variant="unstyled" w="100%" defaultIndex={location.pathname === '/signup' ? 1 : 0}>
                  <TabList
                    justifyContent="center"
                    borderBottom="1px solid #E2E8F0"
                    position="relative"
                  >
                    <Tab
                      _selected={{
                        color: "black",
                        borderBottom: "2px solid black",
                        borderBottomWidth: "2px",
                        marginBottom: "-1px",
                        paddingBottom: "8px",
                      }}
                      color="#666666"
                      paddingBottom="10px"
                      fontSize="16px"
                      fontWeight="500"
                      width="50%"
                    >
                      Sign In
                    </Tab>
                    <Tab
                      _selected={{
                        color: "black",
                        borderBottom: "2px solid black",
                        borderBottomWidth: "2px",
                        marginBottom: "-1px",
                        paddingBottom: "8px",
                      }}
                      color="#666666"
                      paddingBottom="10px"
                      fontSize="16px"
                      fontWeight="500"
                      width="50%"
                    >
                      Create Account
                    </Tab>
                  </TabList>

                  <TabPanels>
                    {/* Login Panel */}
                    <TabPanel p={0} pt={4}>
                      <form onSubmit={handleLogin}>
                        <Stack spacing={4} alignItems="center">
                          <FormControl w="100%">
                            <InputGroup borderRadius="4px">
                              <Input
                                type="text"
                                placeholder="Enter your email"
                                value={loginData.email}
                                required
                                onChange={handleLoginInputChange("email")}
                                onBlur={handleEmailBlur("email", true)}
                                onKeyDown={handleEmailKeyDown("email", true)}
                                height="48px"
                                fontSize="16px"
                                _placeholder={{ color: "#C1C4CE" }}
                                sx={{
                                  "&:-webkit-autofill": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#E2E8F0",
                                  },
                                  "&:-webkit-autofill:hover": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#E2E8F0",
                                  },
                                  "&:-webkit-autofill:focus": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#536DEC",
                                  },
                                }}
                              />
                            </InputGroup>
                          </FormControl>
                          <FormControl w="100%" mb={3}>
                            <InputGroup borderRadius="4px">
                              <Input
                                type={loginData.showPassword ? "text" : "password"}
                                placeholder="Enter password"
                                value={loginData.password}
                                required
                                onChange={handleLoginInputChange("password")}
                                height="48px"
                                fontSize="16px"
                                _placeholder={{ color: "#C1C4CE" }}
                                sx={{
                                  "&:-webkit-autofill": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#E2E8F0",
                                  },
                                  "&:-webkit-autofill:hover": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#E2E8F0",
                                  },
                                  "&:-webkit-autofill:focus": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#536DEC",
                                  },
                                }}
                              />
                              <InputRightElement width="50px" height="48px">
                                <Button
                                  h="15px"
                                  w="15px"
                                  backgroundColor="#FFFFFF"
                                  _hover={{ bg: "#FFFFFF" }}
                                  color="#666666"
                                  p={0}
                                  onClick={handleShowClick}
                                  border="none"
                                >
                                  {loginData.showPassword ? <EyeOff size={12} /> : <Eye size={12} />}
                                </Button>
                              </InputRightElement>
                            </InputGroup>
                          </FormControl>
                          <FormControl w="100%">
                            <Flex
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Flex alignItems="center">
                                <Checkbox
                                  sx={{
                                    ".chakra-checkbox__control": {
                                      width: "24px",
                                      height: "24px",
                                      borderWidth: "1px",
                                      borderColor: "#0A101B",
                                      borderRadius: "2px",
                                    },
                                    ".chakra-checkbox__icon": {
                                      fontSize: "16px",
                                    },
                                  }}
                                  onChange={() => setLoginData(prev => ({
                                    ...prev,
                                    keepMeSignedIn: !prev.keepMeSignedIn
                                  }))}
                                />
                                <Text pl={2} fontWeight={500} fontSize="16px">
                                  Keep me signed in
                                </Text>
                              </Flex>
                              <Link
                                href="/forgotPassword"
                                fontSize="16px"
                                fontWeight={400}
                                color="#234E94"
                                textDecoration="underline"
                                _hover={{ textDecoration: "underline" }}
                              >
                                Forgot password?
                              </Link>
                            </Flex>
                          </FormControl>
                          <Button
                            mt={3}
                            borderRadius="4px"
                            type="submit"
                            variant="solid"
                            _hover={{ bg: "#535DEC" }}
                            backgroundColor="#536DEC"
                            w="100%"
                            height="48px"
                            fontSize="16px"
                            fontWeight="600"
                            isLoading={loginData.loginLoading}
                          >
                            Log in
                          </Button>
                        </Stack>
                      </form>
                    </TabPanel>

                    <TabPanel p={0} pt={4}>
                      <form onSubmit={handleSignup}>
                        <Stack spacing={4} alignItems="center">
                          <Flex w="100%" gap={3} direction={{ base: "column", md: "row" }}>
                            <FormControl flex={1} minWidth="120px" isRequired>
                              <InputGroup borderRadius="4px">
                                <Input
                                  type="text"
                                  placeholder="First name"
                                  value={signupData.name}
                                  required
                                  onChange={handleInputChange("name")}
                                  height="48px"
                                  fontSize="16px"
                                  _placeholder={{ color: "#C1C4CE" }}
                                  sx={{
                                    "&:-webkit-autofill": {
                                      WebkitBoxShadow: "0 0 0 1000px white inset",
                                      WebkitTextFillColor: "#000000",
                                      borderColor: "#E2E8F0",
                                    },
                                  }}
                                />
                              </InputGroup>
                            </FormControl>
                            <FormControl flex={1} minWidth="120px" isRequired>
                              <InputGroup borderRadius="4px">
                                <Input
                                  type="text"
                                  placeholder="Last name"
                                  value={signupData.surname}
                                  required
                                  onChange={handleInputChange("surname")}
                                  height="48px"
                                  fontSize="16px"
                                  _placeholder={{ color: "#C1C4CE" }}
                                  sx={{
                                    "&:-webkit-autofill": {
                                      WebkitBoxShadow: "0 0 0 1000px white inset",
                                      WebkitTextFillColor: "#000000",
                                      borderColor: "#E2E8F0",
                                    },
                                  }}
                                />
                              </InputGroup>
                            </FormControl>
                          </Flex>

                          <Flex w="100%" gap={3} direction={{ base: "column", md: "row" }}>
                            <FormControl flex={1} minWidth="120px" isRequired>
                              <InputGroup borderRadius="4px">
                                <Input
                                  type="email"
                                  placeholder="Enter email address"
                                  value={signupData.email}
                                  required
                                  onChange={handleInputChange("email")}
                                  onBlur={handleEmailBlur("email", false)}
                                  onKeyDown={handleEmailKeyDown("email", false)}
                                  height="48px"
                                  fontSize="16px"
                                  _placeholder={{ color: "#C1C4CE" }}
                                  sx={{
                                    "&:-webkit-autofill": {
                                      WebkitBoxShadow: "0 0 0 1000px white inset",
                                      WebkitTextFillColor: "#000000",
                                      borderColor: "#E2E8F0",
                                    },
                                  }}
                                />
                              </InputGroup>
                            </FormControl>
                            <FormControl flex={1} minWidth="120px" isRequired>
                              <InputGroup borderRadius="4px">
                                <Input
                                  type="tel"
                                  placeholder="Enter contact number"
                                  value={signupData.phone_number}
                                  required
                                  onChange={handleInputChange("phone_number")}
                                  height="48px"
                                  fontSize="16px"
                                  _placeholder={{ color: "#C1C4CE" }}
                                  sx={{
                                    "&:-webkit-autofill": {
                                      WebkitBoxShadow: "0 0 0 1000px white inset",
                                      WebkitTextFillColor: "#000000",
                                      borderColor: "#E2E8F0",
                                    },
                                  }}
                                />
                              </InputGroup>
                            </FormControl>
                          </Flex>

                          <FormControl w="100%" isRequired>
                            <InputGroup borderRadius="4px">
                              <Input
                                type={showSignupPassword ? "text" : "password"}
                                placeholder="Create password"
                                value={signupData.password}
                                required
                                onChange={handleInputChange("password")}
                                borderColor={
                                  signupData.password.length > 3 &&
                                  !getPasswordRequirements(
                                    signupData.password,
                                    signupData.confirmPassword
                                  ).isValid
                                    ? "red.300"
                                    : undefined
                                }
                                height="48px"
                                fontSize="16px"
                                _placeholder={{ color: "#C1C4CE" }}
                                sx={{
                                  "&:-webkit-autofill": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#E2E8F0",
                                  },
                                }}
                              />
                              <InputRightElement width="50px" height="48px">
                                <Button
                                  h="15px"
                                  w="15px"
                                  backgroundColor="#FFFFFF"
                                  _hover={{ bg: "#FFFFFF" }}
                                  color="#666666"
                                  p={0}
                                  onClick={handleShowSignupPassword}
                                  border="none"
                                  minW="15px"
                                >
                                  {showSignupPassword ? (
                                    <EyeOff size={12} />
                                  ) : (
                                    <Eye size={12} />
                                  )}
                                </Button>
                                <Tooltip
                                  label={
                                    <Box
                                      bg="white"
                                      p={3}
                                      borderRadius="8px"
                                      boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
                                      border="1px solid #E2E8F0"
                                    >
                                      <Text
                                        fontSize="14px"
                                        fontWeight="600"
                                        mb={2}
                                        color="#171923"
                                      >
                                        Password Requirements:
                                      </Text>
                                      <Stack spacing={1} fontSize="12px">
                                        <Text
                                          color={
                                            getPasswordRequirements(
                                              signupData.password,
                                              signupData.confirmPassword
                                            ).hasMinLength
                                              ? "#38A169"
                                              : "#E53E3E"
                                          }
                                          fontWeight="500"
                                        >
                                          ✓ At least 8 characters
                                        </Text>
                                        <Text
                                          color={
                                            getPasswordRequirements(
                                              signupData.password,
                                              signupData.confirmPassword
                                            ).hasSpecialChar
                                              ? "#38A169"
                                              : "#E53E3E"
                                          }
                                          fontWeight="500"
                                        >
                                          ✓ Special character (!@#$%^&*...)
                                        </Text>
                                        <Text
                                          color={
                                            getPasswordRequirements(
                                              signupData.password,
                                              signupData.confirmPassword
                                            ).hasNumber
                                              ? "#38A169"
                                              : "#E53E3E"
                                          }
                                          fontWeight="500"
                                        >
                                          ✓ Number (0-9)
                                        </Text>
                                        <Text
                                          color={
                                            getPasswordRequirements(
                                              signupData.password,
                                              signupData.confirmPassword
                                            ).hasCapital
                                              ? "#38A169"
                                              : "#E53E3E"
                                          }
                                          fontWeight="500"
                                        >
                                          ✓ Capital letter (A-Z)
                                        </Text>
                                        <Text
                                          color={
                                            getPasswordRequirements(
                                              signupData.password,
                                              signupData.confirmPassword
                                            ).passwordsMatch
                                              ? "#38A169"
                                              : "#E53E3E"
                                          }
                                          fontWeight="500"
                                        >
                                          ✓ Passwords match
                                        </Text>
                                      </Stack>
                                    </Box>
                                  }
                                  placement="right-start"
                                  closeOnClick={false}
                                  bg="transparent"
                                  borderRadius="0"
                                  boxShadow="none"
                                  offset={[30, 0]}
                                  strategy="fixed"
                                  modifiers={[
                                    {
                                    name: 'preventOverflow',
                                      options: {
                                      boundary: 'viewport',
                                        padding: 8,
                                      },
                                    },
                                    {
                                    name: 'flip',
                                      enabled: false,
                                    },
                                  ]}
                                >
                                  <Button
                                    h="15px"
                                    w="15px"
                                    backgroundColor="#FFFFFF"
                                    _hover={{ bg: "#FFFFFF" }}
                                    color={
                                      signupData.password.length > 0 &&
                                      !getPasswordRequirements(
                                        signupData.password,
                                        signupData.confirmPassword
                                      ).isValid
                                        ? "#E53E3E"
                                        : "#666666"
                                    }
                                    p={0}
                                    border="none"
                                    minW="15px"
                                    position="absolute"
                                    right="55px"
                                  >
                                    <Info size={12} />
                                  </Button>
                                </Tooltip>
                              </InputRightElement>
                            </InputGroup>
                          </FormControl>

                          <FormControl w="100%" isRequired>
                            <InputGroup borderRadius="4px">
                              <Input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm password"
                                value={signupData.confirmPassword}
                                required
                                onChange={handleInputChange("confirmPassword")}
                                borderColor={
                                  signupData.confirmPassword &&
                                    signupData.password !==
                                    signupData.confirmPassword
                                    ? "red.300"
                                    : undefined
                                }
                                height="48px"
                                fontSize="16px"
                                _placeholder={{ color: "#C1C4CE" }}
                                sx={{
                                  "&:-webkit-autofill": {
                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                    WebkitTextFillColor: "#000000",
                                    borderColor: "#E2E8F0",
                                  },
                                }}
                              />
                              <InputRightElement width="50px" height="48px">
                                <Button
                                  h="15px"
                                  w="15px"
                                  backgroundColor="#FFFFFF"
                                  _hover={{ bg: "#FFFFFF" }}
                                  color="#666666"
                                  p={0}
                                  onClick={handleShowConfirmPassword}
                                  border="none"
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff size={12} />
                                  ) : (
                                    <Eye size={12} />
                                  )}
                                </Button>
                              </InputRightElement>
                            </InputGroup>
                          </FormControl>

                          <Button
                            mt={3}
                            borderRadius="4px"
                            type="submit"
                            variant="solid"
                            _hover={{ bg: "#535DEC" }}
                            backgroundColor="#536DEC"
                            w="100%"
                            height="48px"
                            fontSize="16px"
                            fontWeight="600"
                            isLoading={signupLoading}
                            isDisabled={!(allFieldsFilled && isPassValid)}
                          >
                            Continue
                          </Button>
                          <FormControl w="100%" mb={2}>
                            <Box textAlign="center">
                              <Text
                                textAlign="center"
                                fontFamily="'Open Sans', sans-serif"
                                fontSize="14px"
                                color="#515961"
                                fontWeight="400"
                              >
                                By signing up, you agree to our{" "}
                                <Text
                                  as="span"
                                  color="#0151FC"
                                  fontWeight="600"
                                  cursor="pointer"
                                  textDecoration="underline"
                                  onClick={() => window.open('https://morelucro.com//policies/terms-and-conditions/', '_blank')}
                                >
                                  Terms of Service
                                </Text>
                                <Text as="span" color="#515961"> and </Text>
                                <Text
                                  as="span"
                                  color="#0151FC"
                                  fontWeight="600"
                                  cursor="pointer"
                                  textDecoration="underline"
                                  onClick={() => window.open('https://morelucro.com/policies/privacy-policy/', '_blank')}
                                >
                                  Privacy Policy
                                </Text>
                                .
                              </Text>
                            </Box>
                          </FormControl>
                        </Stack>
                      </form>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Stack>
            )}
          </Stack>
        </Box>

        <Box
          ref={rightPanelRef}
          display={{ base: "none", lg: "flex" }}
          alignItems="stretch"
          justifyContent="center"
          position="fixed"
          overflow="visible"
          bg="#171923"
          right={0}
          top={0}
          bottom={0}
          width={{ base: "0vw", lg: "55vw" }}
          height="100vh"
          flexDirection="column"
          sx={{
            userSelect: "none",
            pointerEvents: "none",
            touchAction: "none",
            WebkitTouchCallout: "none",
            WebkitTapHighlightColor: "transparent",
            "& img": {
              userSelect: "none",
              pointerEvents: "none",
              touchAction: "none",
            },
            "@media screen and (max-aspect-ratio: 1/1)": {
              display: "none !important"
            }
          }}
        >
          {/* Flex column: dashTitle and spacer */}
          <Box
            ref={dashTitleRef}
            width="100%"
            maxW="none"
            margin="0 auto"
            px={{ base: "5%" }}
            zIndex={10}
            position="absolute"
            left={0}
            right={0}
            style={{
              top: dashTitleTop,
              visibility: metricsLoaded && dashTitleTop !== undefined ? 'visible' : 'hidden',
              transition: 'visibility 0.2s',
            }}
          >
            <Image
              src="/welcomeDashTitle.svg"
              alt=""
              display="block"
              margin="0 auto"
              width={dashTitleSize.width}
              height={dashTitleSize.height}
              onLoad={() => setDashTitleLoaded(true)}
            />
          </Box>
          <Box flex={1} />
          {/* Background decorations */}
          <Image
            src="/welcomeBubblesRight.svg"
            alt=""
            position="absolute"
            top={0}
            right={0}
            zIndex={2}
            width="25%"
            objectFit="contain"
            objectPosition="top right"
          />
          <Image
            src="/welcomeBubblesLeft.svg"
            alt=""
            position="absolute"
            bottom={0}
            left={0}
            zIndex={2}
            width="40%"
            objectFit="contain"
            objectPosition="bottom left"
          />
          {/* Dashboard image absolutely positioned at the bottom */}
          <Box
            ref={metricsRef}
            position="absolute"
            bottom="0"
            left="20%"
            width="80%"
            height="70%"
            zIndex={3}
            overflow="hidden"
            sx={{
              "@media screen and (max-width: 900px)": {
                display: "none"
              }
            }}
          >
            <Image
              src="/welcomeDashMetrics.svg"
              alt=""
              width="100%"
              height="100%"
              objectFit="cover"
              objectPosition="left top"
              draggable={false}
              style={{ userSelect: "none", pointerEvents: "none" }}
              onLoad={() => setMetricsLoaded(true)}
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default WelcomeLegacyForm;
